import {
	UpdateStageLoader,
	type UpdateStage,
} from "@/components/defi/UpdateStageLoader";
import {
	modalVariants,
	overlayVariants,
	withReducedMotion,
} from "@/lib/motion";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

/** Animation #6 — bottom sheet (mobile) / fade+scale modal (desktop). */
export function ConfirmSheet({
	open,
	onOpenChange,
	title,
	titleIcon,
	description,
	children,
	confirmLabel = "Confirm",
	onConfirm,
	isPending,
	confirmDisabled,
	stage = "idle",
	destructive,
	hideFooter = false,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	/** Optional mark next to title (e.g. MotokoDollar icon). */
	titleIcon?: ReactNode;
	description: string;
	children?: ReactNode;
	confirmLabel?: string;
	onConfirm: () => void;
	isPending?: boolean;
	confirmDisabled?: boolean;
	stage?: UpdateStage;
	destructive?: boolean;
	/** Hide Cancel / Confirm bar (content provides its own actions). */
	hideFooter?: boolean;
}) {
	const reduced = useReducedMotion();

	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !isPending) onOpenChange(false);
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener("keydown", onKey);
		};
	}, [open, isPending, onOpenChange]);

	if (typeof document === "undefined") return null;

	// Always center as modal — bottom drawer pushed vault confirm off-screen
	const panelVariants = withReducedMotion(modalVariants, reduced);
	const overlay = withReducedMotion(overlayVariants, reduced);

	return createPortal(
		<AnimatePresence>
			{open ? (
				<div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]">
					<motion.div
						className="absolute inset-0 bg-black/50 backdrop-blur-sm"
						variants={overlay}
						initial="hidden"
						animate="show"
						exit="exit"
						onClick={() => {
							if (!isPending) onOpenChange(false);
						}}
					/>
					<motion.div
						role="dialog"
						aria-modal
						aria-labelledby="confirm-sheet-title"
						className={cn(
							"relative flex w-full max-w-xl flex-col overflow-hidden bg-card shadow-card",
							"max-h-[min(90dvh,900px)] rounded-2xl border border-border",
						)}
						variants={panelVariants}
						initial="hidden"
						animate="show"
						exit="exit"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4 sm:px-6">
							<div className="min-w-0 pr-2">
								<p className="section-eyebrow mb-1">Vault action</p>
								<h2
									id="confirm-sheet-title"
									className="flex flex-wrap items-center gap-2 text-xl font-bold tracking-tight sm:text-2xl"
								>
									{titleIcon}
									<span>{title}</span>
								</h2>
								<p className="mt-2 text-sm leading-relaxed text-muted-foreground">
									{description}
								</p>
							</div>
							<button
								type="button"
								disabled={isPending}
								className="inline-flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground hover:text-foreground disabled:opacity-40"
								onClick={() => onOpenChange(false)}
								aria-label="Close"
							>
								<X className="size-5" />
							</button>
						</div>

						<div className="flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-5 py-5 sm:px-6">
							{/* overflow-x-hidden keeps range thumb from spilling; py leaves room for drag */}
							{children}
							{stage !== "idle" && (
								<div className="rounded-2xl border border-border bg-muted/40 p-4">
									<p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
										Update progress
									</p>
									<UpdateStageLoader stage={stage} />
								</div>
							)}
							<p className="text-[11px] leading-relaxed text-muted-foreground">
								On Internet Computer, writes go through signing → submit →
								consensus. Balances update only after confirmation.
							</p>
						</div>

						{!hideFooter ? (
							<div className="sticky bottom-0 flex gap-3 border-t border-border bg-card/95 px-5 py-4 backdrop-blur-xl sm:px-6">
								<button
									type="button"
									disabled={isPending}
									className="btn-secondary flex-1 py-3.5 text-sm disabled:opacity-50"
									onClick={() => onOpenChange(false)}
								>
									Cancel
								</button>
								<button
									type="button"
									disabled={
										isPending || confirmDisabled || stage === "confirmed"
									}
									className={cn(
										"flex-[1.4] rounded-xl py-3.5 text-sm font-semibold text-primary-foreground disabled:opacity-50",
										destructive ? "bg-danger" : "btn-primary !min-h-0",
									)}
									onClick={onConfirm}
								>
									{isPending ? "Working…" : confirmLabel}
								</button>
							</div>
						) : null}
					</motion.div>
				</div>
			) : null}
		</AnimatePresence>,
		document.body,
	);
}
